// src/App.js
import React from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate
} from 'react-router-dom';
import Home from './pages/Home';
import Listings from './pages/Listings';
import ListingDetail from './pages/ListingDetail';
import ContactUs from './pages/ContactUs';
import SignIn from './pages/SignIn';
import AddListing from './pages/AddListing';
import EditListing from './pages/EditListing';
import MyListings from './pages/MyListings';
import MyCalendar from './pages/MyCalendar';
import Navigation from './components/Navigation';
import ProviderApplication from './pages/ProviderApplication';
import ServiceProviderDashboard from './pages/ServiceProviderDashboard';  // Import the new dashboard
import AdminDashboard from './pages/admin/AdminDashboard';
import ApplicationDetail from './pages/admin/ApplicationDetail';
import AdminUsers from './pages/admin/AdminUsers';
import AdminListings from './pages/admin/AdminListings';
import AdminAddListing from './pages/admin/AdminAddListing';
import ApproveListing from './pages/ApproveListing';
// Import AuthProvider and useAuth
import { AuthProvider, useAuth } from './contexts/AuthContext';
import './index.css';
import './Navigation.css';

// Protected route component for regular authenticated users
function ProtectedRoute({ children }) {
  const { currentUser, loading } = useAuth();
  
  if (loading) return <div>Loading...</div>;
  
  if (!currentUser) {
    return <Navigate to="/signin" />;
  }
  
  return children;
}

// Protected route component for approved service providers
function ProviderRoute({ children }) {
  const { currentUser, isApprovedProvider, loading } = useAuth();
  
  if (loading) return <div>Loading...</div>;
  
  // Redirect to sign in if not authenticated
  if (!currentUser) {
    return <Navigate to="/signin" />;
  }
  
  // Redirect to provider application if not an approved provider
  if (!isApprovedProvider) {
    return <Navigate to="/become-provider" />;
  }
  
  return children;
}

// Protected route component for admin users
function AdminRoute({ children }) {
  const { currentUser, isAdmin, loading } = useAuth();
  
  if (loading) return <div>Loading...</div>;
  
  // Redirect to sign in if not authenticated
  if (!currentUser) {
    return <Navigate to="/signin" />;
  }
  
  // Redirect to home if not an admin
  if (!isAdmin) {
    return <Navigate to="/" />;
  }
  
  return children;
}

function AppContent() {
  const { currentUser, loading } = useAuth();
  
  if (loading) {
    return <div className="app-loading">Loading...</div>;
  }
  
  return (
    <>
      <Navigation />

      {/* Route definitions */}
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/listings" element={<Listings />} />
        <Route path="/listings/:id" element={<ListingDetail />} />
        <Route path="/contact" element={<ContactUs />} />
        <Route path="/signin" element={<SignIn />} />
        
        {/* Provider application */}
        <Route 
          path="/become-provider" 
          element={
            <ProtectedRoute>
              <ProviderApplication />
            </ProtectedRoute>
          } 
        />
        
        {/* My Calendar route */}
        <Route 
          path="/my-calendar" 
          element={
            <ProtectedRoute>
              <MyCalendar />
            </ProtectedRoute>
          } 
        />
        
        {/* New Service Provider Dashboard Route */}
        <Route 
          path="/provider-dashboard" 
          element={
            <ProviderRoute>
              <ServiceProviderDashboard />
            </ProviderRoute>
          } 
        />
        
        {/* Listing management (needs provider approval) */}
        <Route 
          path="/add-listing" 
          element={
            <ProviderRoute>
              <AddListing />
            </ProviderRoute>
          } 
        />
        <Route 
          path="/edit-listing/:id" 
          element={
            <ProviderRoute>
              <EditListing />
            </ProviderRoute>
          } 
        />
        <Route 
          path="/my-listings" 
          element={
            <ProviderRoute>
              <MyListings />
            </ProviderRoute>
          } 
        />
        
        {/* Admin routes */}
        <Route 
          path="/admin" 
          element={
            <AdminRoute>
              <AdminDashboard />
            </AdminRoute>
          } 
        />
        <Route 
          path="/admin/application/:id" 
          element={
            <AdminRoute>
              <ApplicationDetail />
            </AdminRoute>
          } 
        />
        <Route 
          path="/admin/users" 
          element={
            <AdminRoute>
              <AdminUsers />
            </AdminRoute>
          } 
        />
        <Route 
          path="/admin/listings" 
          element={
            <AdminRoute>
              <AdminListings />
            </AdminRoute>
          } 
        />
        <Route 
          path="/admin/add-listing" 
          element={
            <AdminRoute>
              <AdminAddListing />
            </AdminRoute>
          } 
        />
        
        {/* Approval route for new users */}
        <Route 
          path="/approve-listing" 
          element={<ApproveListing />} 
        />
      </Routes>
    </>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

export default App;